import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import {
  forwardRef,
  useId,
  type ButtonHTMLAttributes,
  type HTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type TextareaHTMLAttributes,
} from "react";
import { cn } from "@/lib/utils";

/* -------------------------------------------------------------- Button ---
   Plum ink on cream paper. The primary action is the ink itself — there is no
   accent hue in this brand, so weight and contrast carry the hierarchy. */

// The primary control is a SLAB OF FUEL: cream on plum.
//
// On a plum page the loudest thing available is the liquid itself, so the button that
// signs you in is the same colour as the thing in the tank. It is the only large area of
// cream on the page, which is exactly why the eye goes to it — no accent colour needed,
// and none spent.
const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-[8px] text-sm " +
    "font-medium transition-[background-color,color,border-color,opacity,transform] duration-150 " +
    "active:translate-y-px " +
    "disabled:pointer-events-none disabled:opacity-45 " +
    "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cream " +
    "[&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        primary: "bg-cream text-ink hover:bg-[#fffaf0]",
        secondary:
          "border border-rule-strong bg-paper text-text hover:bg-raised-hi hover:border-mauve",
        ghost: "text-muted hover:bg-paper hover:text-text",
        link: "text-text underline underline-offset-4 hover:text-muted",

        // THERE IS NO `danger` VARIANT, AND THAT IS DELIBERATE.
        //
        // It used to exist, as `bg-dry` — and it was a trap. `dry` is the ONE warm colour in
        // this product and it means exactly one thing: fuel is running out. The moment it
        // also means "delete", it means nothing, and a hospital goes dark because somebody
        // muted an alert that had cried wolf.
        //
        // Two teammates independently reached for it, read DESIGN.md §1, and refused it. The
        // variant was the trap, not their judgement — so it is gone rather than documented
        // around.
        //
        // A destructive action uses `secondary` and carries its weight in the COPY. Name the
        // button what will happen ("Decommission tank", "Remove Omar"), say what survives,
        // and let the sentence do the work a colour cannot: red tells a person to be
        // careful, words tell them what they are about to do.
      },
      size: {
        sm: "h-8 px-3 text-[13px]",
        md: "h-10 px-4",
        lg: "h-11 px-6",
        icon: "size-9",
      },
    },
    defaultVariants: { variant: "primary", size: "md" },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant, size, asChild = false, type = "button", ...props },
  ref,
) {
  const Comp = asChild ? Slot : "button";
  return (
    <Comp
      ref={ref}
      className={cn(buttonVariants({ variant, size }), className)}
      {...(asChild ? {} : { type })}
      {...props}
    />
  );
});

/* --------------------------------------------------------------- Input --- */

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  function Input({ className, ...props }, ref) {
    return (
      <input
        ref={ref}
        className={cn(
          // Sunk, not raised: a field is a well cut INTO the panel, so the caret sits
          // below the surface and the panel reads as a solid object rather than a stack
          // of floating rectangles.
          //
          // h-10, matching Select and Button. It was h-11, and every filter row in the
          // product where a select sat beside a date field was visibly ragged — the labels
          // landed on different baselines. Two teammates patched around it at their own call
          // sites; the primitive was wrong, so it is fixed here instead. Every control in
          // this product is 40px tall.
          "h-10 w-full rounded-[8px] border border-rule-strong bg-paper-sunk px-3 text-sm text-text",
          "transition-[border-color,background-color] duration-150",
          "placeholder:text-faint",
          "hover:border-mauve",
          "focus-visible:border-cream focus-visible:bg-paper focus-visible:outline-2",
          "focus-visible:outline-offset-[-1px] focus-visible:outline-cream",
          "disabled:cursor-not-allowed disabled:opacity-50",
          "aria-[invalid=true]:border-dry aria-[invalid=true]:outline-dry",
          className,
        )}
        {...props}
      />
    );
  },
);

/** Numbers are the product. They are set in the instrument face and they line up. */
export const NumberInput = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  function NumberInput({ className, ...props }, ref) {
    return (
      <Input
        ref={ref}
        type="number"
        inputMode="decimal"
        // Latin digits, LTR, tabular — a figure reads the same in both languages.
        dir="ltr"
        className={cn("font-mono tabular-nums text-end", className)}
        {...props}
      />
    );
  },
);

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(
  function Textarea({ className, ...props }, ref) {
    return (
      <textarea
        ref={ref}
        className={cn(
          "min-h-20 w-full rounded-[6px] border border-rule-strong bg-paper p-3 text-sm text-text",
          "placeholder:text-faint",
          "focus-visible:border-ink focus-visible:outline-2 focus-visible:outline-offset-[-1px]",
          "focus-visible:outline-ink",
          "aria-[invalid=true]:border-dry",
          className,
        )}
        {...props}
      />
    );
  },
);

/* --------------------------------------------------------------- Field ---
   Label, control and error, wired together. Every input in the product goes
   through this: an unlabelled input is not a thing we ship. */

export interface FieldProps {
  label: string;
  /** The RFC 7807 `validation_errors` message for this field, if the server sent one. */
  error?: string | undefined;
  hint?: string;
  required?: boolean;
  children: (props: {
    id: string;
    "aria-invalid": boolean;
    "aria-describedby": string | undefined;
  }) => ReactNode;
  className?: string;
}

export function Field({ label, error, hint, required, children, className }: FieldProps) {
  const id = useId();
  const errorId = `${id}-error`;
  const hintId = `${id}-hint`;

  const describedBy = [error ? errorId : null, hint ? hintId : null]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={cn("flex flex-col gap-1.5", className)}>
      <label htmlFor={id} className="text-sm font-medium text-text">
        {label}
        {required ? (
          <span aria-hidden className="text-dry ms-1">
            *
          </span>
        ) : null}
      </label>

      {children({
        id,
        "aria-invalid": !!error,
        "aria-describedby": describedBy || undefined,
      })}

      {hint ? (
        <p id={hintId} className="text-xs text-faint">
          {hint}
        </p>
      ) : null}

      {error ? (
        // Announced, not just coloured — an error nobody hears is an error nobody fixes.
        <p id={errorId} role="alert" className="text-xs font-medium text-dry">
          {error}
        </p>
      ) : null}
    </div>
  );
}

/* ---------------------------------------------------------------- Card --- */

export function Card({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        "rounded-[6px] border border-rule bg-paper",
        // The sheet on the desk: one hairline, one shadow, nothing else.
        "shadow-[0_1px_2px_rgba(57,54,70,0.04)]",
        className,
      )}
      {...props}
    />
  );
}

export function CardHeader({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn("flex items-center justify-between gap-4 border-b border-rule px-4 py-3", className)}
      {...props}
    />
  );
}

export function CardTitle({ className, ...props }: HTMLAttributes<HTMLHeadingElement>) {
  return <h2 className={cn("text-sm font-semibold text-text weight-display", className)} {...props} />;
}

export function CardBody({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn("p-4", className)} {...props} />;
}

/* --------------------------------------------------------------- Badge ---
   Instrument colours: each says exactly one thing, and never decorates. */

const badgeVariants = cva(
  "inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium whitespace-nowrap",
  {
    variants: {
      tone: {
        dry: "bg-dry-wash text-dry",
        low: "bg-low-wash text-low",
        ok: "bg-ok-wash text-ok",
        idle: "bg-paper-sunk text-muted",
        ink: "bg-ink text-cream",
      },
    },
    defaultVariants: { tone: "idle" },
  },
);

export interface BadgeProps
  extends HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof badgeVariants> {}

export function Badge({ className, tone, ...props }: BadgeProps) {
  return <span className={cn(badgeVariants({ tone }), className)} {...props} />;
}

/* ------------------------------------------------------------ Skeleton --- */

export function Skeleton({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      aria-hidden
      className={cn("animate-pulse rounded-[4px] bg-paper-sunk", className)}
      {...props}
    />
  );
}

/* ---------------------------------------------------- Micro-label / Num --- */

/** The mono micro-label: section eyebrows, table heads, units. */
export function MicroLabel({ className, ...props }: HTMLAttributes<HTMLSpanElement>) {
  return (
    <span
      className={cn("label-caps font-mono text-[10px] font-medium text-faint", className)}
      {...props}
    />
  );
}

/** Any figure the operator might copy onto a delivery note. Tabular, Latin, isolated. */
export function Num({ className, ...props }: HTMLAttributes<HTMLSpanElement>) {
  return <span className={cn("num", className)} {...props} />;
}
